import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Exporta BACKEND_URL para uso em outros componentes
export const getBackendURL = () => {
  if (!BACKEND_URL) {
    console.error('❌ REACT_APP_BACKEND_URL não configurado!');
    return '';
  }
  return BACKEND_URL;
};

// Cria instância do axios
const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000, // 60 segundos para endpoints lentos
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token JWT e auto-refresh
api.interceptors.request.use(
  async (config) => {
    // Verifica ambos storages (localStorage e sessionStorage)
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
    
    if (token) {
      // Decodifica token para verificar expiração
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000; // Converter para milliseconds
        const now = Date.now();
        const timeLeft = expiresAt - now;
        
        // Se falta menos de 5 minutos para expirar, renova
        if (timeLeft < 5 * 60 * 1000 && refreshToken) {
          console.log('🔄 Token expirando em breve, renovando...');
          try {
            const response = await axios.post(`${API_BASE}/admin/refresh`, null, {
              params: { refresh_token: refreshToken }
            });
            
            const { access_token, refresh_token: new_refresh } = response.data;
            const storage = localStorage.getItem('access_token') ? localStorage : sessionStorage;
            storage.setItem('access_token', access_token);
            storage.setItem('refresh_token', new_refresh);
            
            config.headers.Authorization = `Bearer ${access_token}`;
            console.log('✅ Token renovado com sucesso');
          } catch (error) {
            console.error('❌ Erro ao renovar token:', error);
          }
        } else {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (e) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expirado, redireciona para login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// ===== KAKO API =====
export const kakoAPI = {
  verifyUser: (kakoId) => api.post('/kako/verify-user', { kako_id: kakoId })
};

// ===== PACKAGES API =====
export const packagesAPI = {
  list: (activeOnly = true) => api.get('/packages/', { params: { active_only: activeOnly } }),
  get: (id) => api.get(`/packages/${id}`),
  create: (data) => api.post('/packages/', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`)
};

// ===== ORDERS API =====
export const disputesAPI = {
  summary: () => api.get('/disputes/summary'),
  list: (params) => api.get('/disputes/', { params }),
  release: (id, note) => api.post(`/disputes/${id}/release`, { note }),
  block: (id, note) => api.post(`/disputes/${id}/block`, { note }),
  refresh: (id) => api.post(`/disputes/${id}/refresh`),
  medConfig: () => api.get('/disputes/efi/config'),
  medConfigUpdate: (data) => api.put('/disputes/efi/config', data),
  medSync: (days = 3) => api.post('/disputes/efi/sync', null, { params: { days } }),
  medImportHistory: (days = 365) =>
    api.post('/disputes/efi/import-history', null, { params: { days } }),
  medRematch: () => api.post('/disputes/efi/rematch'),
  mpWatch: () => api.get('/disputes/mercadopago/watch'),
  mpWatchUpdate: (enabled) => api.put('/disputes/mercadopago/watch', null,
    { params: { enabled } }),
  mpWatchRun: () => api.post('/disputes/mercadopago/watch/run'),
  mpSearch: (days = 365) => api.post('/disputes/mercadopago/search', null, { params: { days } }),
  defensePreview: (id) => api.get(`/disputes/${id}/defense-preview`),
  defend: (id, text) => api.post(`/disputes/${id}/defense`, { text }),
};

export const ordersAPI = {
  create: (data) => api.post('/orders/', data),
  list: (params) => api.get('/orders/', { params }),
  listByDate: (params) => api.get('/orders/by-date', { params }),
  get: (id) => api.get(`/orders/${id}`),
  checkStatus: (id) => api.post(`/orders/${id}/check-status`),
  confirmByReceipt: (id, data) => api.post(`/orders/${id}/confirm-by-receipt`, data),
  investigateE2E: (e2e) => api.get('/orders/investigate-e2e', { params: { e2e } }),
  paymentIntegrity: (days = 7) => api.get('/orders/payment-integrity', { params: { days } }),
  paymentAlerts: () => api.get('/orders/payment-alerts'),
  timeline: (id) => api.get(`/orders/${id}/timeline`),
  updateStatus: (id, newStatus) => api.patch(`/orders/${id}/status`, null, { params: { new_status: newStatus } }),
  deleteBulk: (orderIds) => api.delete('/orders/bulk', { data: orderIds }),
  getRecentPurchases: (limit = 20) => api.get('/orders/recent-purchases', { params: { limit } })
};

// ===== PAYMENT METHODS API =====
export const paymentMethodsAPI = {
  getConfig: () => api.get('/payment-methods/config'),
  updateConfig: (data) => api.put('/payment-methods/config', data),
  getAvailable: () => api.get('/payment-methods/available')
};

// ===== CONFIG API =====
export const configAPI = {
  get: () => api.get('/config/'),
  update: (data) => api.put('/config/', null, { params: data })
};

// ===== DASHBOARD API =====
export const dashboardAPI = {
  getStats: (days = 7) => api.get('/dashboard/stats', { params: { days } }),
  getRecentOrders: (limit = 10) => api.get('/dashboard/recent-orders', { params: { limit } }),
  getBalances: (days = 9999) => api.get('/dashboard/balances', { params: { days } }),
  getPeakHours: (days = 30) => api.get('/dashboard/peak-hours', { params: { days } }),
  getLostRewards: () => api.get('/dashboard/lost-rewards'),
  exportMonthlyReport: async (year, month) => {
    const response = await api.get('/dashboard/monthly-report', {
      params: { year, month },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio_${year}_${String(month).padStart(2, '0')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

// ===== TASKS API =====
export const tasksAPI = {
  listActive: () => api.get('/tasks/active'),
  getProgress: (kakoId) => api.get(`/tasks/progress/${kakoId}`),
  claimReward: (kakoId, taskId) => api.post('/tasks/claim-reward', { kako_id: kakoId, task_id: taskId }),
  getMyClaims: (kakoId, date) => api.get(`/tasks/my-claims/${kakoId}`, { params: { date } }),
  listAll: () => api.get('/tasks/'),
  create: (data) => api.post('/tasks/', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  getClaimHistory: (params) => api.get('/tasks/claim-history', { params }),
  deleteClaimsBulk: (claimIds) => api.post('/tasks/claims/delete-bulk', claimIds)
};

// ===== RANKING API =====
export const rankingAPI = {
  getCurrent: (limit = 10) => api.get('/ranking/current', { params: { limit } }),
  getMyPosition: (kakoId) => api.get(`/ranking/my-position/${kakoId}`),
  getWinners: (limit = 30) => api.get('/ranking/winners', { params: { limit } }),
  getMyPrizes: (kakoId) => api.get(`/ranking/my-prizes/${kakoId}`),
  getPrizesInfo: () => api.get('/ranking/prizes-info'),
  claimPrize: (kakoId, date) => api.post('/ranking/claim-prize', { kako_id: kakoId, date }),
  removeUser: (kakoId, date) => api.delete(`/ranking/user/${kakoId}/date/${date}`),
  getClaimsHistory: (params) => api.get('/ranking/claims-history', { params }),
  deleteClaimsBulk: (claimIds) => api.post('/ranking/claims/delete-bulk', claimIds),
  getHistory: (days = 30) => api.get('/ranking/history', { params: { days } }),
  getConfig: () => api.get('/ranking/config'),
  updateConfig: (data) => api.put('/ranking/config', data),
  finalizeDay: (date) => api.post('/ranking/finalize-day', null, { params: { date } })
};

// ===== ADMIN API =====
export const adminAPI = {
  register: (data) => api.post('/admin/register', data),
  login: (data) => api.post('/admin/login', data),
  getMe: () => api.get('/admin/me'),
  getProfile: () => api.get('/admin/profile'),
  changePassword: (data) => api.put('/admin/change-password', data),
  enable2FA: () => api.post('/admin/enable-2fa'),
  verify2FASetup: (code) => api.post('/admin/verify-2fa-setup', null, { params: { code } }),
  disable2FA: (code) => api.post('/admin/disable-2fa', null, { params: { code } }),
  refresh: (refreshToken) => api.post('/admin/refresh', null, { params: { refresh_token: refreshToken } }),
  // Gerenciamento de Administradores
  listAdmins: () => api.get('/admin/list'),
  createAdmin: (data) => api.post('/admin/create', data),
  deleteAdmin: (adminId) => api.delete(`/admin/delete/${adminId}`)
};

// ===== FINANCE API =====
export const financeAPI = {
  getConfig: () => api.get('/finance/config'),
  updateConfig: (data) => api.put('/finance/config', data),
  getSummary: (days = 7) => api.get('/finance/summary', { params: { days } })
};

// ===== ADVERTISEMENTS API =====
export const advertisementsAPI = {
  listActive: () => api.get('/advertisements/active'),
  listAll: () => api.get('/advertisements/'),
  create: (data) => api.post('/advertisements/', data),
  update: (id, data) => api.put(`/advertisements/${id}`, data),
  delete: (id) => api.delete(`/advertisements/${id}`),
  uploadChunk: (data) => api.post('/advertisements/upload-chunk', data),
  uploadComplete: (data) => api.post('/advertisements/upload-complete', data),
  reorder: (orderList) => api.post('/advertisements/reorder', orderList)
};

// ===== PLAYLIST API =====
export const playlistAPI = {
  listActive: () => api.get('/playlist/active'),
  listAll: () => api.get('/playlist/'),
  create: (data) => api.post('/playlist/', data),
  update: (id, data) => api.put(`/playlist/${id}`, data),
  delete: (id) => api.delete(`/playlist/${id}`),
  reorder: (orderList) => api.post('/playlist/reorder', orderList)
};

// ===== PAYMENT CONFIRMATION AD API =====
export const paymentAdAPI = {
  get: () => api.get('/payment-ad/'),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/payment-ad/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  update: (data) => api.put('/payment-ad/', data),
  deleteImage: (filename) => api.delete('/payment-ad/image', { params: { filename } })
};

// ===== RESELLER API =====
export const resellerAPI = {
  getRankingAdmin: () => api.get('/reseller/ranking/admin'),
  getRankingWeekly: () => api.get('/reseller/ranking/weekly'),
  // Histórico imutável de vendas dos afiliados (admin)
  migrateSalesFromOrders: () => api.post('/reseller/admin/sales/migrate-from-orders'),
  getSalesHistory: (params) => api.get('/reseller/admin/sales/history', { params }),
  bulkUpdateVisibility: (reseller_ids, can_view_customers) => api.post('/reseller/admin/bulk-update-visibility', { reseller_ids, can_view_customers })
};

// ===== APPLICATIONS API =====
export const applicationsAPI = {
  getConfig: () => api.get('/applications/config'),
  checkStatus: (kakoId) => api.get(`/applications/status/${kakoId}`),
  create: (data) => api.post('/applications/', data),
  list: (statusFilter = 'pending') => api.get('/applications/', { params: { status_filter: statusFilter } }),
  approve: (applicationId) => api.post(`/applications/${applicationId}/approve`),
  reject: (applicationId, reason = '') => api.post(`/applications/${applicationId}/reject`, null, { params: { reason } }),
  deleteBulk: (applicationIds) => api.delete('/applications/bulk', { data: applicationIds }),
  getCurrentWinners: () => api.get('/applications/winners/current'),
  getWinnersHistory: (weeks = 4) => api.get('/applications/winners/history', { params: { weeks } }),
  deleteWinnersBulk: (winnerIds) => api.delete('/applications/winners/bulk', { data: winnerIds })
};

// ===== MEGA PRIZE API =====
export const megaPrizeAPI = {
  getConfig: () => api.get('/mega-prize/config'),
  updateConfig: (data) => api.put('/mega-prize/config', null, { params: data }),
  getMyTickets: (kakoId) => api.get(`/mega-prize/tickets/my/${kakoId}`),
  getAllTickets: (params) => api.get('/mega-prize/tickets/all', { params }),
  addTicket: (data) => api.post('/mega-prize/tickets/add', null, { params: data }),
  deleteTickets: (ticketIds) => api.delete('/mega-prize/tickets/bulk', { data: ticketIds }),
  exportTicketsCSV: async () => {
    const response = await api.get('/mega-prize/tickets/export-csv', {
      responseType: 'blob'
    });
    
    // Cria download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'mega_premio_tickets_completo.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  deleteWinnersBulk: (winnerIds) => api.delete('/mega-prize/winners/bulk', { data: winnerIds }),
  finalize: (drawnNumbers) => api.post('/mega-prize/finalize', { drawn_numbers: drawnNumbers }),
  restart: (clearData = false) => api.post(`/mega-prize/restart?clear_data=${clearData}`),
  getCurrentWinner: () => api.get('/mega-prize/winner/current'),
  getWinnersHistory: (limit = 10) => api.get('/mega-prize/winners/history', { params: { limit } })
};

// Mystery Box (Baú Misterioso) API
export const mysteryBoxAPI = {
  // Admin
  getConfig: () => api.get('/mystery-box/config'),
  updateConfig: (data) => api.put('/mystery-box/config', data),
  getStats: () => api.get('/mystery-box/stats'),
  getPrizes: () => api.get('/mystery-box/prizes'),
  getFixedPrizeList: () => api.get('/mystery-box/prizes/fixed-list'),
  updatePrizeQuantity: (prizeId, totalQuantity) => api.put(`/mystery-box/prizes/${prizeId}/quantity`, { total_quantity: totalQuantity }),
  getAdminHistory: (params) => api.get('/mystery-box/admin/history', { params }),
  resetEvent: () => api.post('/mystery-box/admin/reset-event'),
  clearHistory: () => api.post('/mystery-box/admin/clear-history'),
  clearJackpotHistory: () => api.post('/mystery-box/admin/clear-jackpot-history'),
  generateWeeklyPrizes: (week) => api.post('/mystery-box/admin/generate-weekly-prizes', null, { params: { week } }),
  dedupeWeeklyPrizes: () => api.post('/mystery-box/admin/weekly-prizes/dedupe'),
  getPrizesTotalValue: () => api.get('/mystery-box/prizes/total-value'),
  getWeeklyWinners: () => api.get('/mystery-box/admin/weekly-winners'),
  
  // Jackpot Admin
  getJackpotConfig: () => api.get('/mystery-box/jackpot/config'),
  updateJackpotConfig: (data) => api.put('/mystery-box/jackpot/config', data),
  resetJackpot: () => api.post('/mystery-box/jackpot/reset'),
  
  // Jackpot Sequences
  getJackpotSequences: () => api.get('/mystery-box/jackpot/sequences'),
  createJackpotSequence: (data) => api.post('/mystery-box/jackpot/sequences', data),
  updateJackpotSequence: (sequenceId, data) => api.put(`/mystery-box/jackpot/sequences/${sequenceId}`, data),
  deleteJackpotSequence: (sequenceId) => api.delete(`/mystery-box/jackpot/sequences/${sequenceId}`),
  reorderJackpotSequences: (newOrder) => api.put('/mystery-box/jackpot/sequences/reorder', newOrder),
  resetJackpotSequencesProgress: () => api.post('/mystery-box/jackpot/sequences/reset-progress'),
  
  // Client
  getProgress: (kakoId) => api.get(`/mystery-box/progress/${kakoId}`),
  openBox: (kakoId) => api.post(`/mystery-box/open/${kakoId}`),
  getHistory: (kakoId) => api.get(`/mystery-box/history/${kakoId}`),
  getRules: () => api.get('/mystery-box/rules'),
  getTopWinners: (period = 'current') => api.get('/mystery-box/top-winners', { params: { period } }),
  getMyWeeklyPrizes: (kakoId) => api.get(`/mystery-box/weekly-ranking/my-prizes/${kakoId}`),
  claimWeeklyPrize: (prizeId) => api.post(`/mystery-box/weekly-ranking/claim/${prizeId}`, null, {
    headers: { Authorization: `Bearer ${localStorage.getItem('customer_token')}` },
  }),
  
  // Jackpot Client
  getJackpotStatus: (kakoId) => api.get('/mystery-box/jackpot/status', { params: { kako_id: kakoId } }),
  claimJackpot: (kakoId, nickname, avatar) => api.post('/mystery-box/jackpot/claim', null, { 
    params: { kako_id: kakoId, kako_nickname: nickname, kako_avatar: avatar }
  })
};

export const rouletteAdminAPI = {
  prizeAlerts: () => api.get('/event-campaigns/roulette/prize-alerts'),
};

export default api;
